import { ModalStyled } from '@/components/ModalAppliedItemsWithTags/styled';
import { checkShowErrorInline } from '@/helpers';
import { stringify } from '@/helpers/string';
import useUserPlan from '@/hooks/useUserPlan';
import { apiCaller } from '@/redux/query';
import ruleSlice, { IRuleError } from '@/redux/slice/rule.slice';
import { IOptions } from '@/types';
import { IParamsApi } from '@/types/apis/params';
import {
  BlockStack,
  Button,
  Checkbox,
  Divider,
  Icon,
  IconSource,
  Image,
  InlineStack,
  LegacyStack,
  Modal,
  Scrollable,
  Tag,
  Text,
  TextField,
} from '@shopify/polaris';
import { InfoIcon, RefreshIcon, SearchIcon } from '@shopify/polaris-icons';
import { Fragment, memo, useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import BoldSmallText from '../BoldSmallText';
import BoldText from '../BoldText';
import CriticalBanner from '../CriticalBanner';
import InformationBanner from '../InformationBanner';
import RegularText from '../RegularText';
import { useDispatch } from 'react-redux';
import checkoutRuleSlice, { ICheckoutRuleError } from '@/redux/slice/checkoutRule.slice';

interface IProps {
  buttonAndModalTitle: string;
  buttonIcon?: IconSource;
  footerCountText: string;
  options: IOptions[];
  selectedOptions: IOptions[];
  setSelectedOptions: (value: IOptions[], variants?: IParamsApi.IVariantsProduct[]) => void;
  tag: IOptions[];
  isRequired?: boolean;
  disabled?: boolean;
  handleSearch?: (value: string) => void;
  variantsProducts?: IParamsApi.IVariantsProduct[];
  selectedVariantsProduct?: IParamsApi.IVariantsProduct[];
  error?: IRuleError | ICheckoutRuleError;
  handleSetError?: (isClear: boolean) => void;
  handleClearInputSearch?: () => void;
  desc?: string;
  isDefaultOpen?: boolean;
  isInCheckout?: boolean;
}

const ModalAppliedItemsWithTags = (props: IProps) => {
  const dispatch = useDispatch();
  const currentSlice = useMemo(() => {
    return props.isInCheckout ? checkoutRuleSlice : ruleSlice;
  }, [props.isInCheckout]);
  const isMobile = useMediaQuery({ maxWidth: 650 });
  const [isOpenModal, setIsOpenModal] = useState(!!props.isDefaultOpen);
  const [inputSearch, setInputSearch] = useState('');
  const [options, setOptions] = useState<IOptions[]>([]);
  const [backupOptions, setBackupOptions] = useState<IOptions[]>([]);
  const [tempSelectedOptions, setTempSelectedOptions] = useState<IOptions[]>([]);
  const [variantsProducts, setVariantsProduct] = useState<IParamsApi.IVariantsProduct[]>([]);
  const [tempSelectedVariantsProduct, setTempSelectedVariantsProduct] = useState<IParamsApi.IVariantsProduct[]>([]);
  const { isFreePlan } = useUserPlan();
  const [banner, setBanner] = useState({
    critical: { isVisible: false, msg: '' },
    isVisibleInfoBanner: false,
  });

  const [sync, { isLoading: isLoadingSync, isSuccess }] = apiCaller.useSyncProductAndCollectionMutation();

  useEffect(() => {
    if (props.isDefaultOpen) setIsOpenModal(true);
  }, [props.isDefaultOpen]);

  useEffect(() => {
    if (!isOpenModal) {
      setInputSearch('');
      props?.handleClearInputSearch?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenModal]);

  useEffect(() => {
    setOptions([...props.options]);
    setBackupOptions([...props.options]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stringify(props.options)]);

  useEffect(() => {
    if (props.variantsProducts) setVariantsProduct([...props.variantsProducts]);
  }, [props.variantsProducts]);

  useEffect(() => {
    if (!isOpenModal) props.selectedVariantsProduct && setTempSelectedVariantsProduct(props.selectedVariantsProduct);
  }, [isOpenModal, props.selectedVariantsProduct]);

  useEffect(() => {
    if (!isOpenModal) props.selectedOptions && setTempSelectedOptions(props.selectedOptions);
  }, [isOpenModal, props.selectedOptions]);

  const updateText = (value: string) => {
    setInputSearch(value);
    if (props.handleSearch) {
      props.handleSearch(value);
    } else {
      const resultOptions = value
        ? backupOptions?.filter((option) => option.label.toLowerCase().includes(value.toLowerCase()))
        : backupOptions;
      setOptions(resultOptions);

      const resultVariantsProduct = variantsProducts?.reduce((acc: IParamsApi.IVariantsProduct[], vp) => {
        const filteredVariants = vp.variants.filter((v) => v.name.toLowerCase().includes(value.toLowerCase()));
        if (filteredVariants.length) {
          acc.push({ ...vp, variants: filteredVariants });
        } else if (resultOptions.some((option) => option.value === vp.idProduct)) {
          acc.push(vp);
        }
        return acc;
      }, []);
      setVariantsProduct(resultVariantsProduct);
    }
  };

  const handleError = (isInValid: boolean) => {
    props.handleSetError && props.handleSetError(isInValid);
  };

  const removeTag = (tag: string) => () => {
    const newTags = props.selectedOptions?.filter((item) => item.value !== tag);
    const newVariantsProduct = tempSelectedVariantsProduct?.filter((item) => item.idProduct !== tag);
    handleError(!!newTags?.length);
    props.setSelectedOptions(newTags, newVariantsProduct);
  };

  const tagsMarkup =
    props.tag.length > 0
      ? props.tag.map((tag) => {
          const variantNames = props?.selectedVariantsProduct
            ?.find((item) => item.idProduct === tag.value)
            ?.variants?.map((variant) => variant.name);
          const totalVariants = props?.selectedVariantsProduct?.find((item) => item.idProduct === tag.value)?.totalVariants;
          return (
            <Tag key={`option-${tag.value}`} onRemove={removeTag(tag.value)} disabled={props.disabled}>
              <BoldSmallText>{tag.label || ''}</BoldSmallText>
              {totalVariants && !!variantNames?.length && variantNames.length < totalVariants
                ? `: ${variantNames?.join(', ')}`
                : ''}
            </Tag>
          );
        })
      : null;

  const selectedTagMarkup = props.selectedOptions.length > 0 && (
    <div className="mt-8">
      <LegacyStack spacing="extraTight">{tagsMarkup}</LegacyStack>
    </div>
  );

  const handleClose = () => {
    setBanner({ ...banner, critical: { isVisible: false, msg: '' } });
    setIsOpenModal(false);
    handleError(!!tempSelectedOptions.length);
    dispatch(currentSlice.actions.handleResetIsDefaultOpenModalAddCondition());
  };

  const handleSave = () => {
    props.setSelectedOptions(tempSelectedOptions, tempSelectedVariantsProduct);
    handleError(!!tempSelectedOptions.length);
    setIsOpenModal(false);
    dispatch(currentSlice.actions.handleResetIsDefaultOpenModalAddCondition());
  };

  const handleSync = () => {
    sync().then((res) => {
      const checkError = checkShowErrorInline(res);
      if (checkError.status) {
        setBanner({ ...banner, critical: { isVisible: true, msg: checkError.msg } });
      } else {
        setBanner({ ...banner, isVisibleInfoBanner: true });
      }
    });
  };

  const variantCount = useMemo(() => {
    if (!props.variantsProducts) {
      return tempSelectedOptions.length;
    }
    let total = 0;
    tempSelectedOptions.forEach((option) => {
      if (option.value) {
        const findVariant = tempSelectedVariantsProduct?.find((item) => item.idProduct === option.value)?.variants;
        total += findVariant ? findVariant?.length || 1 : 1;
      }
    });
    return total;
  }, [props.variantsProducts, tempSelectedOptions, tempSelectedVariantsProduct]);

  return (
    <div className={isMobile ? 'mt-8' : ''}>
      <div className="mt-12">
        <Button icon={props.buttonIcon} onClick={() => setIsOpenModal(true)}>
          {props.buttonAndModalTitle}
        </Button>
      </div>
      {props.error ? (
        <div className="d-flex align-center mt-8 mb-8 gap-4">
          <Icon source={InfoIcon} tone="critical" />
          <Text as="p" variant="bodyMd" tone="critical">
            {props.error.msg}
          </Text>
        </div>
      ) : null}
      <div style={{ marginTop: '8px' }}>{selectedTagMarkup}</div>
      <Modal
        title={props.buttonAndModalTitle}
        open={isOpenModal}
        onClose={handleClose}
        footer={tempSelectedOptions.length > 0 ? <BoldText>{variantCount + ' ' + props.footerCountText}</BoldText> : undefined}
        primaryAction={{
          content: 'Save',
          onAction: handleSave, // Commit changes when Add is clicked
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: handleClose,
          },
        ]}
      >
        <Modal.Section>
          <ModalStyled>
            {props?.desc ? (
              <div className="mb-8">
                <Text as="p" variant="bodyMd">
                  {props.desc}
                </Text>
              </div>
            ) : null}

            <div className="mb-16">
              <InlineStack blockAlign="center" gap="300">
                <div style={{ flexGrow: 1 }}>
                  <TextField
                    label=""
                    autoComplete="off"
                    placeholder="Search"
                    prefix={<Icon source={SearchIcon} />}
                    value={inputSearch}
                    onChange={updateText}
                    clearButton
                    onClearButtonClick={() => updateText('')}
                    autoFocus
                  />
                </div>
                <Button icon={RefreshIcon} onClick={handleSync} loading={isLoadingSync}>
                  Sync
                </Button>
              </InlineStack>
            </div>

            <InformationBanner
              isVisible={isSuccess && banner.isVisibleInfoBanner}
              onDismiss={() => setBanner({ ...banner, isVisibleInfoBanner: false })}
            >
              Items are being synced from Shopline and will take <strong>1 - 2 minutes</strong>. You can continue other setup
              while you wait.
            </InformationBanner>

            <CriticalBanner isVisible={banner.critical.isVisible}>{banner.critical.msg}</CriticalBanner>

            <Scrollable style={{ maxHeight: '55vh' }}>
              <BlockStack>
                {options.map((option, index) => {
                  const variants = props.variantsProducts?.find((v) => v.idProduct === option.value)?.variants;
                  const tempSelectedVariants = tempSelectedVariantsProduct?.find((v) => v.idProduct === option.value)?.variants;
                  const isSelectedOption = tempSelectedOptions.some((tso) => tso.value === option.value);
                  let checked: boolean | 'indeterminate' = false;
                  if (tempSelectedVariants && props.variantsProducts) {
                    if (tempSelectedVariants?.length === variants?.length) {
                      checked = true;
                    } else if (tempSelectedVariants?.length === 0) {
                      checked = false;
                    } else {
                      checked = 'indeterminate';
                    }
                  } else {
                    checked = isSelectedOption;
                  }

                  return (
                    <Fragment key={option.value}>
                      <div className="item-container">
                        <Checkbox
                          disabled={isFreePlan && !checked}
                          checked={checked} // Temporary state for checkboxes
                          onChange={() => {
                            if (checked === true) {
                              setTempSelectedOptions(tempSelectedOptions.filter((tso) => tso.value !== option.value));
                              props.variantsProducts &&
                                setTempSelectedVariantsProduct((prev) => prev.filter((tso) => tso.idProduct !== option.value));
                            } else {
                              setTempSelectedOptions([
                                ...tempSelectedOptions.filter((tso) => tso.value !== option.value),
                                option,
                              ]);
                              props.variantsProducts &&
                                setTempSelectedVariantsProduct([
                                  ...tempSelectedVariantsProduct.filter((tso) => tso.idProduct !== option.value),
                                  {
                                    idProduct: option.value,
                                    variants: variants || [],
                                    totalVariants: variants?.length,
                                  },
                                ]);
                            }
                          }}
                          label={
                            <InlineStack blockAlign="center">
                              {option.image ? (
                                <div className="image-container">
                                  <Image
                                    style={{
                                      width: 40,
                                      height: 40,
                                      background: '#FDFDFD',
                                      boxShadow: '0px 0px 0px 1px rgba(0, 0, 0, 0.08) inset',
                                      borderRadius: 8,
                                    }}
                                    alt={option.label}
                                    source={option.image}
                                  />
                                </div>
                              ) : null}
                              <BoldText>{option.label}</BoldText>
                            </InlineStack>
                          }
                        />
                      </div>
                      {index < options.length - 1 && <Divider />}
                      {variants
                        ? variants.map((variant) => {
                            // hide default variant
                            return variant.name !== 'Default Title' ? (
                              <div className="item-container item-variant" key={variant.id}>
                                <Checkbox
                                  disabled={isFreePlan && !tempSelectedVariants?.some((tsv) => tsv.id === variant.id) && !checked}
                                  checked={tempSelectedVariants?.some((tsv) => tsv.id === variant.id)} // Temporary state for checkboxes
                                  onChange={(newChecked) => {
                                    const updatedVariants = newChecked
                                      ? [...(tempSelectedVariants || []), variant]
                                      : (tempSelectedVariants || []).filter((tsv) => tsv.id !== variant.id);

                                    const newTempSelectedVariantsProduct = [
                                      ...tempSelectedVariantsProduct.filter((tsvp) => tsvp.idProduct !== option.value),
                                      { idProduct: option.value, variants: updatedVariants, totalVariants: variants?.length },
                                    ].filter((ts) => ts?.variants?.length > 0);

                                    setTempSelectedVariantsProduct(newTempSelectedVariantsProduct);

                                    setTempSelectedOptions(
                                      newChecked
                                        ? [...tempSelectedOptions.filter((tso) => tso.value !== option.value), option]
                                        : tempSelectedVariants?.length === 1
                                        ? tempSelectedOptions.filter((tso) => tso.value !== option.value)
                                        : tempSelectedOptions,
                                    );
                                  }}
                                  label={
                                    <InlineStack blockAlign="center">
                                      <RegularText>{variant.name}</RegularText>
                                    </InlineStack>
                                  }
                                />
                              </div>
                            ) : null;
                          })
                        : null}
                    </Fragment>
                  );
                })}
              </BlockStack>
            </Scrollable>
          </ModalStyled>
        </Modal.Section>
      </Modal>
    </div>
  );
};

export default memo(ModalAppliedItemsWithTags);
